import React, { useCallback, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPagination, GridPaginationModel } from '@mui/x-data-grid';

import * as LoyaltyService from '~/services/LoyaltyService';

import {
  DEFAULT_PAGE_SIZE,
  MANUAL_ACCRUAL_STATUS_OPTIONS,
  type ManualAccrual,
  type ManualAccrualStatus,
} from '~/types/loyaltyContract';

import ManualAccrualDetailDialog from './ManualAccrualDetailDialog';
import RetryManualAccrualsDialog from './RetryManualAccrualsDialog';

export default function LoyaltyManualAccrualsPage() {
  const queryClient = useQueryClient();

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filter state
  const [customerIdInput, setCustomerIdInput] = useState('');
  const [orderIdInput, setOrderIdInput] = useState('');
  const [itemIdInput, setItemIdInput] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<ManualAccrualStatus | ''>('');

  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState<{
    customerId?: string;
    orderId?: string;
    itemId?: string;
    status?: ManualAccrualStatus;
  }>({});

  // Dialog state
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedAccrualForDetail, setSelectedAccrualForDetail] = useState<ManualAccrual | null>(null);

  // Row selection state
  const [rowSelectionModel, setRowSelectionModel] = useState<Array<string>>([]);

  // Query for fetching manual accruals
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['loyalty', 'manual-accruals', paginationModel],
    queryFn: async ({ signal }) => {
      return await LoyaltyService.getManualAccruals(
        {
          page: paginationModel.page + 1, // API uses 1-based indexing
          pageSize: paginationModel.pageSize,
        },
        { signal },
      );
    },
    placeholderData: (previousData) => previousData,
    retry: false,
  });

  const pageInfo = data?.pageInfo;
  const totalCount: number = pageInfo?.pageCount ? pageInfo.pageCount * paginationModel.pageSize : 0;

  // Client-side filtering based on applied filters
  const filteredAccruals = useMemo(() => {
    const accruals = data?.result || [];
    return accruals.filter((accrual) => {
      if (appliedFilters.customerId && !accrual.customerId.includes(appliedFilters.customerId)) {
        return false;
      }
      if (appliedFilters.orderId && !accrual.orderId.includes(appliedFilters.orderId)) {
        return false;
      }
      if (appliedFilters.itemId && !accrual.itemId.includes(appliedFilters.itemId)) {
        return false;
      }
      if (appliedFilters.status && accrual.manualAccrualStatus !== appliedFilters.status) {
        return false;
      }
      return true;
    });
  }, [data?.result, appliedFilters]);

  const handleSearch = (): void => {
    setAppliedFilters({
      customerId: customerIdInput.trim() || undefined,
      orderId: orderIdInput.trim() || undefined,
      itemId: itemIdInput.trim() || undefined,
      status: selectedStatus || undefined,
    });
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleClearFilters = (): void => {
    setCustomerIdInput('');
    setOrderIdInput('');
    setItemIdInput('');
    setSelectedStatus('');
    setAppliedFilters({});
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  };

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  const handleRetryDialogClose = useCallback(() => {
    setIsRetryDialogOpen(false);
  }, []);

  const handleRetrySuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['loyalty', 'manual-accruals'] });
    setRowSelectionModel([]);
    refetch();
  }, [queryClient, refetch]);

  const handleRetrySelected = useCallback(() => {
    if (rowSelectionModel.length > 0) {
      setIsRetryDialogOpen(true);
    }
  }, [rowSelectionModel]);

  const handleViewDetails = useCallback((accrual: ManualAccrual) => {
    setSelectedAccrualForDetail(accrual);
    setIsDetailDialogOpen(true);
  }, []);

  const handleDetailDialogClose = useCallback(() => {
    setIsDetailDialogOpen(false);
    setSelectedAccrualForDetail(null);
  }, []);

  const handleRetryFromDetail = useCallback(() => {
    if (selectedAccrualForDetail) {
      setIsDetailDialogOpen(false);
      setRowSelectionModel([selectedAccrualForDetail.id]);
      setIsRetryDialogOpen(true);
    }
  }, [selectedAccrualForDetail]);

  // Get selected accrual objects from IDs
  const selectedAccruals = useMemo(() => {
    return filteredAccruals.filter((accrual) => rowSelectionModel.includes(accrual.id));
  }, [filteredAccruals, rowSelectionModel]);

  const getStatusColor = (status: ManualAccrualStatus): 'warning' | 'success' | 'error' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'PROCESSED':
        return 'success';
      case 'FAILED':
        return 'error';
      default:
        return 'warning';
    }
  };

  const columns: Array<GridColDef<ManualAccrual>> = useMemo(
    () => [
      {
        field: 'manualAccrualStatus',
        headerName: 'Status',
        width: 120,
        renderCell: ({ value }) => <Chip label={value} color={getStatusColor(value)} size="small" />,
      },
      {
        field: 'customerId',
        headerName: 'Customer ID',
        width: 120,
        renderCell: ({ value }) => (
          <Link
            to={`/users/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {value}
          </Link>
        ),
      },
      {
        field: 'orderId',
        headerName: 'Order ID',
        width: 120,
        renderCell: ({ value }) => (
          <Link
            to={`/purchases/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {value}
          </Link>
        ),
      },
      {
        field: 'itemId',
        headerName: 'Item ID',
        width: 120,
        renderCell: ({ value, row }) => (
          <Link
            to={`/purchases/${row.orderId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {value}
          </Link>
        ),
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        width: 150,
      },
      {
        field: 'pointsFromEstimate',
        headerName: 'Est. Points',
        width: 110,
        type: 'number',
        renderCell: ({ row }) => {
          const total = row.pointsFromEstimate + row.bonusPointsFromEstimate;
          return <Typography variant="body2">{total}</Typography>;
        },
      },
      {
        field: 'pendingPoints.totalPoints',
        headerName: 'Pending Points',
        width: 130,
        type: 'number',
        valueGetter: (value, row) => {
          if (!row.pendingPoints) {
            return null;
          }
          return row.pendingPoints.points + row.pendingPoints.bonusPoints;
        },
        renderCell: ({ value }) => {
          if (value === null || value === undefined) {
            return <Typography variant="body2">-</Typography>;
          }
          return <Typography variant="body2">{value}</Typography>;
        },
      },
      {
        field: 'errorMessage',
        headerName: 'Error',
        width: 200,
        flex: 1,
        renderCell: ({ value }) => {
          if (!value) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Tooltip title={value}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {value}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 110,
        valueFormatter: (value) => {
          if (!value) {
            return '';
          }
          return dayjs(value).format('DD/MM/YY');
        },
      },
    ],
    [],
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Manual Points Accruals</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetrySelected}
          disabled={rowSelectionModel.length === 0}
        >
          Retry Selected ({rowSelectionModel.length})
        </Button>
      </Box>

      {/* Search Form */}
      <Box component="form" onSubmit={(e) => e.preventDefault()} mt={2}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Customer ID"
              value={customerIdInput}
              onChange={(e) => setCustomerIdInput(e.target.value)}
              placeholder="Search by customer ID"
              fullWidth
            />
            <TextField
              label="Order ID"
              value={orderIdInput}
              onChange={(e) => setOrderIdInput(e.target.value)}
              placeholder="Search by order ID"
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Item ID"
              value={itemIdInput}
              onChange={(e) => setItemIdInput(e.target.value)}
              placeholder="Search by item ID"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e: SelectChangeEvent<ManualAccrualStatus | ''>) =>
                  setSelectedStatus(e.target.value as ManualAccrualStatus | '')
                }
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic' }}>All</Typography>
                </MenuItem>
                {MANUAL_ACCRUAL_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Button variant="contained" color="primary" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="text" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Box mt={2}>
          <Alert severity="error">
            Error loading manual accruals: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      {/* Results Table */}
      <Box mt={4}>
        <DataGrid
          rows={filteredAccruals}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 20, 50, 100]}
          rowCount={totalCount}
          slots={{
            pagination: GridPagination,
          }}
          autoHeight
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnMenu
          getRowId={(row) => row.id}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection.map((id) => String(id)))}
          onRowClick={(params) => handleViewDetails(params.row)}
          keepNonExistentRowsSelected
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
          }}
        />
      </Box>

      <ManualAccrualDetailDialog
        open={isDetailDialogOpen}
        onClose={handleDetailDialogClose}
        accrual={selectedAccrualForDetail}
        onRetry={handleRetryFromDetail}
      />

      <RetryManualAccrualsDialog
        open={isRetryDialogOpen}
        onClose={handleRetryDialogClose}
        selectedAccruals={selectedAccruals}
        onSuccess={handleRetrySuccess}
      />
    </Box>
  );
}
